// client/src/components/payroll/SalarySlipModal.jsx
import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { PDFDownloadLink } from '@react-pdf/renderer';
import SalarySlipPDF from './SalarySlipPDF';
import { Download, Printer, FileText, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import './SalarySlipModal.css';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const fmt = (v) => `PKR ${Number(v || 0).toLocaleString()}`;

export default function SalarySlipModal({ show, onHide, data }) {
  if (!data) return null;

  const monthNum = Number(data.month || new Date().getMonth() + 1);
  const monthName = MONTH_NAMES[monthNum - 1] || 'Current Month';
  const yearNum   = Number(data.year || new Date().getFullYear());

  const empName   = data.employee?.name || data.employeeName || 'Staff Member';
  const empRole   = data.employee?.role || data.employeeRole || 'Agent';
  const empEmail  = data.employee?.email || 'N/A';
  const empIdStr  = data.employeeId || data.employee?.id
    ? `PAJ-EMP-${String(data.employeeId || data.employee?.id).padStart(3, '0')}`
    : 'PAJ-EMP-000';

  const fixedSal  = Number(data.fixedSalary || 0);
  const commAmt   = Number(data.commission || 0);
  const bonusAmt  = Number(data.bonuses || 0);
  const grossPay  = fixedSal + commAmt + bonusAmt;

  const deductAmt = Number(data.deductions || 0);
  const netPay    = grossPay - deductAmt;

  const carsCount = Number(data.carsSold || 0);
  const profitGen = Number(data.profitGenerated || 0);
  const isPaid    = (data.paymentStatus || '').toLowerCase() === 'paid';
  const slipNo    = `PAJ-PS-${yearNum}${String(monthNum).padStart(2, '0')}-${String(data.employeeId || data.employee?.id || 1).padStart(3, '0')}`;

  const handlePrint = () => {
    window.print();
  };

  const fileName = `SalarySlip_${empName.replace(/\s+/g, '_')}_${monthName}_${yearNum}.pdf`;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      dialogClassName="salary-slip-modal-dialog"
      contentClassName="salary-slip-modal-content"
    >
      {/* ── Modal Header with Download & Print Actions ── */}
      <Modal.Header className="salary-slip-header border-0 pb-0">
        <div className="d-flex align-items-center gap-3">
          <div className="salary-slip-header-icon">
            <FileText size={20} />
          </div>
          <div>
            <h5 className="salary-slip-title mb-0">Official Salary Pay Slip</h5>
            <div className="salary-slip-subtitle">
              {empName} · {monthName} {yearNum} · Platinum Auto Japan Co., Ltd.
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {/* Official PDF Download Link */}
          <PDFDownloadLink
            document={<SalarySlipPDF data={data} />}
            fileName={fileName}
          >
            {({ loading }) => (
              <Button
                variant="primary"
                size="sm"
                disabled={loading}
                className="d-flex align-items-center gap-1 salary-slip-action-btn"
                title="Download Official PDF Document"
              >
                <Download size={14} />
                <span>{loading ? 'Preparing PDF...' : 'Download PDF'}</span>
              </Button>
            )}
          </PDFDownloadLink>

          {/* Browser Direct Print Button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePrint}
            className="d-flex align-items-center gap-1 salary-slip-action-btn salary-slip-print-btn"
            title="Print or Save via Browser"
          >
            <Printer size={14} />
            <span>Print Slip</span>
          </Button>

          <button
            type="button"
            className="btn-close btn-close-white ms-2"
            onClick={onHide}
            aria-label="Close"
          />
        </div>
      </Modal.Header>

      <Modal.Body className="salary-slip-body pt-3">
        {/* ── Printable Slip Container ── */}
        <div className="salary-slip-printable-card" id="salary-slip-printable-area">
          {/* ── Corporate Letterhead ── */}
          <div className="slip-print-head">
            <div className="d-flex align-items-center gap-3">
              <img
                src="/logo.png"
                alt="Platinum Auto Japan"
                className="slip-print-logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                }}
              />
              <div className="slip-company-info">
                <div className="slip-company-name">PLATINUM AUTO JAPAN CO., LTD.</div>
                <div className="slip-company-sub">Global Japanese Automotive Export &amp; Auction Procurement Operations</div>
                <div className="slip-company-reg">Tokyo &amp; Yokohama Head Office · Corporate Tax ID: PAJ-JP-8829104-FIN</div>
              </div>
            </div>

            <div className="slip-doc-badge-box text-end">
              <span className="slip-confidential-pill">CONFIDENTIAL DOCUMENT</span>
              <div className="slip-main-title">SALARY PAY SLIP</div>
              <div className="slip-period-label">{monthName.toUpperCase()} {yearNum}</div>
            </div>
          </div>

          {/* ── Metadata Strip ── */}
          <div className="slip-meta-strip">
            <div className="slip-meta-item">
              <span className="slip-meta-kicker">Statement No.</span>
              <strong className="slip-meta-val">{slipNo}</strong>
            </div>
            <div className="slip-meta-item">
              <span className="slip-meta-kicker">Pay Period</span>
              <strong className="slip-meta-val">{monthName} {yearNum}</strong>
            </div>
            <div className="slip-meta-item">
              <span className="slip-meta-kicker">Generated On</span>
              <strong className="slip-meta-val">{new Date().toLocaleDateString('en-GB')}</strong>
            </div>
            <div className="slip-meta-item">
              <span className="slip-meta-kicker">Payment Status</span>
              <span className={`slip-status-badge ${isPaid ? 'paid' : 'pending'}`}>
                {isPaid ? (
                  <><CheckCircle2 size={12} className="me-1" /> PAID</>
                ) : (
                  <><Clock size={12} className="me-1" /> PENDING</>
                )}
              </span>
            </div>
          </div>

          {/* ── Employee Details Card ── */}
          <div className="slip-emp-box">
            <div className="slip-emp-head">
              EMPLOYEE IDENTITY &amp; ALLOCATION
            </div>
            <div className="slip-emp-grid">
              <div className="slip-emp-cell">
                <span className="slip-cell-lbl">Full Name:</span>
                <span className="slip-cell-val">{empName}</span>
              </div>
              <div className="slip-emp-cell">
                <span className="slip-cell-lbl">Employee ID:</span>
                <span className="slip-cell-val">{empIdStr}</span>
              </div>
              <div className="slip-emp-cell">
                <span className="slip-cell-lbl">Designation:</span>
                <span className="slip-cell-val">{empRole}</span>
              </div>
              <div className="slip-emp-cell">
                <span className="slip-cell-lbl">Work Email:</span>
                <span className="slip-cell-val">{empEmail}</span>
              </div>
              <div className="slip-emp-cell">
                <span className="slip-cell-lbl">Division:</span>
                <span className="slip-cell-val">{empRole === 'Manager' ? 'Sales Management' : 'Global Vehicle Sales'}</span>
              </div>
              <div className="slip-emp-cell">
                <span className="slip-cell-lbl">Currency:</span>
                <span className="slip-cell-val">Pakistani Rupee (PKR)</span>
              </div>
            </div>
          </div>

          {/* ── Compensation Breakdown (Earnings vs Deductions) ── */}
          <div className="slip-tables-grid">
            {/* Earnings */}
            <div className="slip-table-pane">
              <div className="slip-table-pane-header">
                <span>EARNINGS &amp; ALLOWANCES</span>
                <span>AMOUNT (PKR)</span>
              </div>
              <div className="slip-table-pane-body">
                <div className="slip-entry-row">
                  <div>
                    <div className="slip-entry-title">Basic Fixed Monthly Salary</div>
                    <div className="slip-entry-sub">Agreed monthly base retainer</div>
                  </div>
                  <div className="slip-entry-num">{fmt(fixedSal)}</div>
                </div>

                <div className="slip-entry-row alt">
                  <div>
                    <div className="slip-entry-title">Deal &amp; Vehicle Sales Commission</div>
                    <div className="slip-entry-sub">{carsCount} verified vehicle unit(s) closed</div>
                  </div>
                  <div className="slip-entry-num">{fmt(commAmt)}</div>
                </div>

                <div className="slip-entry-row">
                  <div>
                    <div className="slip-entry-title">Performance Bonuses &amp; Incentives</div>
                    <div className="slip-entry-sub">Monthly target achievement bonus</div>
                  </div>
                  <div className="slip-entry-num">{fmt(bonusAmt)}</div>
                </div>
              </div>
              <div className="slip-table-pane-footer">
                <span>Gross Total Earnings</span>
                <span>{fmt(grossPay)}</span>
              </div>
            </div>

            {/* Deductions */}
            <div className="slip-table-pane">
              <div className="slip-table-pane-header">
                <span>DEDUCTIONS &amp; ADJUSTMENTS</span>
                <span>AMOUNT (PKR)</span>
              </div>
              <div className="slip-table-pane-body">
                <div className="slip-entry-row">
                  <div>
                    <div className="slip-entry-title">Advance Salary Recovery</div>
                    <div className="slip-entry-sub">Prior disbursements or drawdowns</div>
                  </div>
                  <div className="slip-entry-num text-danger">{deductAmt > 0 ? `-${fmt(deductAmt)}` : 'PKR 0'}</div>
                </div>

                <div className="slip-entry-row alt">
                  <div>
                    <div className="slip-entry-title">Statutory Withholdings &amp; Levies</div>
                    <div className="slip-entry-sub">Applicable tax &amp; regional charges</div>
                  </div>
                  <div className="slip-entry-num">PKR 0</div>
                </div>

                <div className="slip-entry-row">
                  <div>
                    <div className="slip-entry-title">Attendance Penalties</div>
                    <div className="slip-entry-sub">Unexcused leaves or policy deductions</div>
                  </div>
                  <div className="slip-entry-num">PKR 0</div>
                </div>
              </div>
              <div className="slip-table-pane-footer">
                <span>Total Deductions</span>
                <span className="text-danger">{deductAmt > 0 ? `-${fmt(deductAmt)}` : 'PKR 0'}</span>
              </div>
            </div>
          </div>

          {/* ── Net Payable Banner ── */}
          <div className="slip-net-banner">
            <div>
              <div className="slip-net-kicker">NET REMUNERATION · 差引支給額</div>
              <div className="slip-net-main-lbl">TOTAL NET PAYABLE SALARY</div>
            </div>
            <div className="text-end">
              <div className="slip-net-val">{fmt(netPay)}</div>
              <div className="slip-net-sub">Cleared Electronic Remittance · PKR</div>
            </div>
          </div>

          {/* ── Operational Note & Contribution ── */}
          <div className="slip-notes-pane">
            <div className="d-flex align-items-center gap-2 mb-1">
              <ShieldCheck size={14} style={{ color: 'var(--primary, #38BDF8)' }} />
              <strong className="slip-notes-heading">Performance Contribution &amp; Audit Trail</strong>
            </div>
            <div className="slip-notes-desc">
              Closed {carsCount} vehicle sale(s) in {monthName} {yearNum}, delivering {fmt(profitGen)} in company revenue.
              {data.notes && <div><strong>Administrative Memo:</strong> {data.notes}</div>}
            </div>
          </div>

          {/* ── Signatures & Authorization ── */}
          <div className="slip-signatures-row">
            <div className="slip-sig-item">
              <div className="slip-sig-line" />
              <div className="slip-sig-name">{empName}</div>
              <div className="slip-sig-role">Employee Signature &amp; Acknowledgement</div>
            </div>

            <div className="slip-sig-item text-end">
              <div className="slip-sig-line" />
              <div className="slip-sig-name">Director of Finance &amp; Operations</div>
              <div className="slip-sig-role">Platinum Auto Japan Co., Ltd. Authorization</div>
            </div>
          </div>

          {/* ── Legal & Confidentiality Footer ── */}
          <div className="slip-legal-footer">
            <span>Generated securely via Platinum CRM Enterprise Payroll Engine.</span>
            <span className="fw-semibold">CONFIDENTIAL &amp; PROPRIETARY</span>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
